import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import i18n from 'i18n-react';
import { useDebounceCallback } from 'usehooks-ts';

import APIHandler from 'config/APIHandler';

import { isMobileWebview } from 'helpers/isMobileWebview';

import { useCustomFilters } from 'hooks/useCustomFilters';
import type { DirectoryConfig } from 'hooks/useDirectoryConfig/types';
import { useDirectoryConfig } from 'hooks/useDirectoryConfig/useDirectoryConfig';
import { useDirectoryData } from 'hooks/useDirectoryData/useDirectoryData';
import { useSettings } from 'hooks/useSettings';

import CategoryFilter from './components/CategoryFilter';
import CustomFilters from './components/CustomFilters';
import DirectoryHeader from './components/DirectoryHeader';
import DirectoryMap from './components/DirectoryMap';
import HotelsList from './components/HotelsList';
import LegacyDirectory from './components/LegacyDirectory';
import ProfileIframe from './components/ProfileIframe';
import { BREAKPOINTS, getContainerStyle } from './constants';

import 'simplebar/src/simplebar.css';
import './_directoryView.scss';

const ENABLE_LOCAL_CONFIG_JSON = false; // Set to true to load config from config.json instead of API. FOR TESTING ONLY!!!
const ENABLE_LOCAL_FILTERS_JSON = false; // Set to true to load categories/customFilters from a local mock instead of the API. FOR TESTING ONLY!!!

// Inner component — only rendered once config is resolved, so useDirectoryData
// always receives a non-null config and useState initializers never crash.
interface DirectoryViewContentProps {
  config: DirectoryConfig;
}

// Shared by CategoryFilter and CustomFilters so both use the identical variant vocabulary.
function getFilterVariant(
  showMap: boolean,
  useDropdownFilter: boolean | undefined,
): 'checkbox' | 'inline' | 'dropdown' {
  if (!showMap) return 'inline';
  return useDropdownFilter ? 'dropdown' : 'checkbox';
}

function DirectoryViewContent({ config }: DirectoryViewContentProps) {
  const { appId, appInfo, email } = useSettings();

  useEffect(() => {
    if (!appId) return;
    void APIHandler.trackEvent(
      appId,
      email ?? 'Guest',
      'collateral',
      'view-directory',
      'view-directory',
      'view-directory',
    );
  }, [appId, email]);

  const { isMobile, showHeader, isSource } = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);

    return {
      isMobile: urlParams.get('mobile')?.toLowerCase() === 'true',
      showHeader: urlParams.get('header')?.toLowerCase() !== 'false',
      isSource: urlParams.get('source')?.toLowerCase() === 'true',
    };
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const containerStyle = useMemo(() => {
    return getContainerStyle(isMobile);
  }, [isMobile]);
  const [userInfo, setUserInfo] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const initialMapVisible = window.innerWidth > BREAKPOINTS.showMap;
  const [showMap, setShowMap] = useState(initialMapVisible);

  const {
    premiumHotelsList,
    freeHotelsList,
    categories,
    customFilters,
    loading,
    selectedCategory,
    selectedSubcategory,
    zoom,
    center,
    language,
    setQuery,
    setSelectedCategory,
    setSelectedSubcategory,
    setZoom,
    setCenter,
    setMapVisible,
    updateBounds,
    resetMap,
    resetFilters,
  } = useDirectoryData(config, initialMapVisible, ENABLE_LOCAL_FILTERS_JSON);

  const {
    values: customFilterValues,
    pendingValues: customFilterPendingValues,
    setFilterValue: setCustomFilterValue,
    setPendingValue: setCustomFilterPendingValue,
    commitPending: commitCustomFilterPending,
    resetAll: resetCustomFilters,
    getVisibleFilters: getVisibleCustomFilters,
    filteredFreeHotelsList,
  } = useCustomFilters(customFilters, freeHotelsList, selectedCategory);

  // Merge so any filter not yet touched via pending still shows its committed value —
  // mirrors how pendingCategory/pendingSubcategory seed from selectedCategory/selectedSubcategory.
  const customFilterDisplayValues = !showMap
    ? { ...customFilterValues, ...customFilterPendingValues }
    : customFilterValues;

  const filterVariant = getFilterVariant(showMap, config.map.useDropdownFilter);

  const updateRightSideWidth = useCallback(() => {
    const rightSide = document.querySelector<HTMLElement>('.directoryView .rightSide');
    if (containerRef.current) {
      if (rightSide) {
        containerRef.current.style.setProperty('--right-side-width', `${rightSide.offsetWidth}px`);
      } else {
        containerRef.current.style.removeProperty('--right-side-width');
      }
    }
  }, []);

  useEffect(() => {
    updateRightSideWidth();
    window.addEventListener('resize', updateRightSideWidth);
    return () => {
      window.removeEventListener('resize', updateRightSideWidth);
    };
  }, [updateRightSideWidth]);

  useEffect(() => {
    const handleResizeCrossing = () => {
      const newWidth = window.innerWidth;
      const nowShowing = newWidth > BREAKPOINTS.showMap;
      setMapVisible(nowShowing);
      setShowMap((prev) => {
        if (prev && !nowShowing) {
          updateBounds(
            config.map.initialBounds.lat1,
            config.map.initialBounds.lat2,
            config.map.initialBounds.lon1,
            config.map.initialBounds.lon2,
            config.map.zoom,
          );
        }
        return nowShowing;
      });
    };
    window.addEventListener('resize', handleResizeCrossing);
    return () => {
      window.removeEventListener('resize', handleResizeCrossing);
    };
  }, [setMapVisible, updateBounds, config.map.initialBounds, config.map.zoom]);

  const handleInputChange = () => {
    const newQuery = searchRef.current?.value ?? '';
    setQuery(newQuery);
  };

  const handleCategorySelect = (category: Parameters<typeof setSelectedCategory>[0]) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
  };

  const handleSubcategorySelect = (subcategory: Parameters<typeof setSelectedSubcategory>[0]) => {
    setSelectedSubcategory(subcategory);
  };

  // Commits both category/subcategory (CategoryFilter's own pending state) and any pending
  // custom-filter selections, then collapses the mobile sheet. Only ever invoked from
  // CategoryFilter's 'inline' variant.
  const handleInlineFiltersSubmit = (
    category: Parameters<typeof setSelectedCategory>[0],
    subcategory: Parameters<typeof setSelectedSubcategory>[0],
  ) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    commitCustomFilterPending();
    setFilterOpen(false);
  };

  // Discards both category/subcategory and any custom-filter selections (committed + pending).
  const handleInlineFiltersReset = () => {
    resetFilters();
    resetCustomFilters();
  };

  const handleBoundsChanged = useCallback(
    (bounds: google.maps.LatLngBounds, newZoom: number) => {
      if (window.innerWidth <= BREAKPOINTS.showMap) return;
      updateBounds(
        bounds.getNorthEast().lat(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getSouthWest().lng(),
        newZoom,
      );
    },
    [updateBounds],
  );

  // The map fires onIdle (which calls this) repeatedly while its container is resizing — e.g. dragging
  // a window edge continuously resizes the map, each step settling into its own idle event. Debounced so
  // a resize gesture triggers one hotels refetch once it settles, not one per idle event along the way.
  const debouncedHandleBoundsChanged = useDebounceCallback(handleBoundsChanged, 400);

  useEffect(() => {
    return () => {
      debouncedHandleBoundsChanged.cancel();
    };
  }, [debouncedHandleBoundsChanged]);

  const handleMapLoad = useCallback(() => {
    updateRightSideWidth();
  }, [updateRightSideWidth]);

  const flyToMarker = (lat: number | string, lon: number | string) => {
    setZoom(config.map.flyToZoom);
    setCenter({ lat: Number(lat), lng: Number(lon) });
  };

  const buildProfileUrl = (id: string, type: 'free' | 'premium') => {
    const colorRaw = appInfo.primaryColor ?? 'e74094';
    const color = colorRaw.startsWith('#') ? colorRaw.slice(1) : colorRaw;

    const url = new URL(`/directory/listing/${id}`, window.location.origin);
    url.searchParams.set('type', type);
    url.searchParams.set('appId', `${appId}`);
    url.searchParams.set('lang', language);
    url.searchParams.set('color', color);
    url.searchParams.set('font', 'Source Sans Pro');
    url.searchParams.set('zoom', '10');
    url.searchParams.set('table', config.listings.table);
    // Propagate webview-context params so the profile view/webview behaves the same as the directory it was opened from.
    url.searchParams.set('mobile', isMobile.toString());
    url.searchParams.set('header', showHeader.toString());
    url.searchParams.set('source', isSource.toString());

    return `${url.pathname}${url.search}`;
  };

  const openProfile = (url: string) => {
    if (isMobileWebview()) {
      window.ReactNativeWebView?.postMessage(`webview:${url}`);
    } else {
      document.getElementById('iframe')?.classList.remove('hide');
      setUserInfo(url);
    }
  };

  const closeIframe = () => {
    document.getElementById('iframe')?.classList.add('hide');
  };

  const handleHotelsListPremiumInfo = (id: string, name: string) => {
    void APIHandler.trackEvent(appId, email ?? 'Guest', 'collateral', 'directory-listing', name, id);
    openProfile(buildProfileUrl(id, 'premium'));
  };

  const handleHotelsListFreeInfo = (id: string, name: string) => {
    void APIHandler.trackEvent(appId, email ?? 'Guest', 'collateral', 'directory-listing', name, id);
    openProfile(buildProfileUrl(id, 'free'));
  };

  const handleMapPremiumInfo = (id: string, name: string) => {
    void APIHandler.trackEvent(appId, email ?? 'Guest', 'collateral', 'directory-listing', name);
    openProfile(buildProfileUrl(id, 'premium'));
  };

  const handleMapFreeInfo = (id: string, name: string) => {
    void APIHandler.trackEvent(appId, email ?? 'Guest', 'collateral', 'directory-listing', name);
    openProfile(buildProfileUrl(id, 'free'));
  };

  return (
    <div className="directoryContainer" ref={containerRef}>
      {!isMobile && showHeader && <DirectoryHeader source={isSource} logoWhite={appInfo.logoWhite} />}

      <div className={`directoryView${isMobile ? ' mobile' : ''}`}>
        <div className="leftSide">
          <div className="results">
            {i18n.translate('directoryView.amountOfResults')}
            {filteredFreeHotelsList.length + premiumHotelsList.length}
            {!showMap && (
              <button
                className="filter-btn"
                onClick={() => {
                  setFilterOpen(!filterOpen);
                }}
              >
                {i18n.translate('directoryView.filter')}
              </button>
            )}
          </div>

          <div className="leftGrid">
            {(!isMobile || !showHeader) && (
              <div className="search">
                <FontAwesomeIcon icon={['fas', 'search']} className="searchIcon" />
                <input
                  className="searchInput"
                  placeholder={i18n.translate('directoryView.search') as string}
                  type="text"
                  ref={searchRef}
                  onChange={handleInputChange}
                />
              </div>
            )}

            <CategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              filterOpen={filterOpen}
              onCategorySelect={handleCategorySelect}
              onSubcategorySelect={handleSubcategorySelect}
              onSubmit={handleInlineFiltersSubmit}
              onResetFilters={handleInlineFiltersReset}
              onToggleFilter={() => {
                setFilterOpen(false);
              }}
              variant={filterVariant}
            >
              {(activeCategory) => {
                const visibleCustomFilters = getVisibleCustomFilters(activeCategory, customFilterDisplayValues);
                return (
                  visibleCustomFilters.length > 0 && (
                    <CustomFilters
                      filters={visibleCustomFilters}
                      values={customFilterDisplayValues}
                      onChange={!showMap ? setCustomFilterPendingValue : setCustomFilterValue}
                      locale={language}
                      variant={filterVariant}
                    />
                  )
                );
              }}
            </CategoryFilter>

            <HotelsList
              premiumHotelsList={premiumHotelsList}
              freeHotelsList={filteredFreeHotelsList}
              loading={loading}
              filterOpen={filterOpen}
              zoom={zoom}
              cardFields={config.cardFields}
              onFlyToMarker={flyToMarker}
              onPremiumInfo={handleHotelsListPremiumInfo}
              onFreeInfo={handleHotelsListFreeInfo}
              onResetMap={resetMap}
            />
          </div>
        </div>

        {showMap && (
          <DirectoryMap
            googleMapsApiKey={config.map.googleMapsApiKey}
            containerStyle={containerStyle}
            center={center}
            zoom={zoom}
            premiumHotelsList={premiumHotelsList}
            freeHotelsList={filteredFreeHotelsList}
            onBoundsChanged={debouncedHandleBoundsChanged}
            onMarkerClick={flyToMarker}
            showMarkerTooltip={config.map.showMarkerTooltip}
            onPremiumInfo={handleMapPremiumInfo}
            onFreeInfo={handleMapFreeInfo}
            onMapLoad={handleMapLoad}
          />
        )}

        <ProfileIframe userInfo={userInfo} onClose={closeIframe} />
      </div>
    </div>
  );
}

// Shell — waits for config before rendering content
function DirectoryView() {
  const state = useDirectoryConfig(ENABLE_LOCAL_CONFIG_JSON);
  if (state.status === 'loading') return null;
  if (state.status === 'not_found') return <LegacyDirectory />;
  return <DirectoryViewContent config={state.config} />;
}

export default DirectoryView;
